import { ViewController } from 'elc-service';
import { emit, events } from 'elc-service-bus';
import { observable, computed, action, when } from 'mobx';
import { Config } from '../domain/entities/Config';
import { ProductRepository } from '../repositories/ProductRepository';
import { POWER_REVIEWS_SCRIPT } from '../constants/PowerReviews';

export class ProductReviewsViewController extends ViewController {
    @observable public isLoading: boolean = true;
    @observable public config: Config;
    @observable public productRepository: ProductRepository;
    @observable public powerReviewsScript: HTMLScriptElement;
    @observable public activeTab: string = 'tab-1';

    constructor(config: Config, productRepository: ProductRepository) {
        super();
        this.config = config;
        this.productRepository = productRepository;

        when(
            () => this.noReviews,
            // tslint:disable-next-line: no-void-expression
            () => this.showReviewsOnViewportIntersection()
        );
    }

    @computed
    get data() {
        return {
            isLoading: this.isLoading,
            activeTab: this.activeTab
        };
    }

    @computed
    public get productId() {
        return this.productRepository.productId;
    }

    @computed
    public get product() {
        return this.productRepository.getProductById(this.productId);
    }

    @computed
    public get numberOfReviewsValue(): number {
        return this.product && this.product.reviews && this.product.reviews.numberOfReviews;
    }

    @computed
    public get noReviews(): boolean {
        const reviewsValue = this.numberOfReviewsValue === null || this.numberOfReviewsValue === 0;

        return Boolean(reviewsValue);
    }

    @action
    public setPaginationType = (paginationType: string) => {
        this.productRepository.setPaginationType(paginationType);
    };

    @action
    public onTabClick = (event: React.MouseEvent<HTMLElement>) => {
        const target = <HTMLElement>event.target;
        this.activeTab = target.dataset.tab || '';
    };

    @action
    public loadReviews = (powerReviewsScript: HTMLScriptElement) => {
        this.isLoading = false;
        // tslint:disable-next-line: no-floating-promises
        this.loaded().then(() => {
            this.loadPowerReviewsScripts(powerReviewsScript);
        });
    };

    @action
    public loadPowerReviewsScripts(powerReviewsScript: HTMLScriptElement) {
        const bodyFirstScript = <HTMLScriptElement>document.querySelector('body > script');
        const parentNode = <HTMLScriptElement>bodyFirstScript.parentNode;
        powerReviewsScript.type = 'text/javascript';
        powerReviewsScript.src = POWER_REVIEWS_SCRIPT;
        powerReviewsScript.defer = true;
        powerReviewsScript.async = true;
        parentNode.insertBefore(powerReviewsScript, bodyFirstScript.nextSibling);

        this.powerReviewsScript = powerReviewsScript;
    }

    public showReviewsOnViewportIntersection = () => {
        const reviewsWrapper = document.querySelector('.elc-product-reviews');
        const prScript = document.createElement('script');

        const intersectionObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.intersectionRatio > 0 && entry.isIntersecting) {
                    if (this.powerReviewsScript && this.powerReviewsScript.src) {
                        intersectionObserver.disconnect();
                        if (reviewsWrapper) {
                            intersectionObserver.unobserve(reviewsWrapper);
                        }
                    } else {
                        this.loadReviews(prScript);
                    }
                } else {
                    if (this.powerReviewsScript && this.powerReviewsScript.src) {
                        intersectionObserver.disconnect();
                        if (reviewsWrapper) {
                            intersectionObserver.unobserve(reviewsWrapper);
                        }
                    } else if (this.noReviews) {
                        this.loadReviews(prScript);
                    }
                }
            },
            {
                root: null,
                rootMargin: '300px',
                threshold: 0.1
            }
        );

        if (reviewsWrapper) {
            intersectionObserver.observe(reviewsWrapper);
        }
    };

    public loaded = () => {
        if (this.productId) {
            this.emitReviewsInit(this.productId);
        }

        return Promise.resolve();
    };

    public emitReviewsInit = (productId: string) => {
        emit(events.SPP_POWER_REVIEWS_INIT, productId);
    };
}
